import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import PermissionProvider from '../../components/PermissionProvider/PermissionProvider';
import {Permission} from '../../store/types/Permission';
import Loader from '../../components/Loader/Loader';
import {actions} from '../../store';
import {useDispatch, useSelector} from 'react-redux';
import {APIErrorInterface, ReduxState} from '../../store/types';
import history from '../../history';
import {Routes} from '../../routes/routes';
import Header from '../../components/ListViewHeader/Header';
import {YesNo} from '../../store/types/CallScreening';
import {PhoneDirectoryRecord} from '../../store/types/Devices';
import DataGrid from '../../components/DataGrid/DataGrid';
import CustomizedIconButton from '../../components/IconButton/IconButton';
import {ReactComponent as ChevronLeft} from '../../assets/chevron left.svg';
import {PaginationMode} from '../../components/DataGrid/types';
import AlertDialog from '../../components/AlertDialog/AlertDialog';
import {DialogButton} from '../../components/AlertDialog/DialogContainer';
import {showToast} from '../../utils/showToast';
import {DevicePhoneBookDetailsProps, generateColumns, useStyle} from './DevicePhoneBookDetails.utils';
import NewPhoneBookEntryDialog from './NewPhoneBookEntryDialog';
import {NewPhoneBookEntryDialogProps} from './NewPhoneBookEntryDialog.utils';
import FormActions from '../../components/Forms/FormActions';
import {Info} from '@material-ui/icons';
import MoreMenu from '../../components/MoreMenu/MoreMenu';
import {MenuItem} from '@material-ui/core';
import {RenameDevicePhoneBookDialogProps} from './RenameDevicePhoneBookDialog.utils';
import RenameDevicePhoneBookDialog from './RenameDevicePhoneBookDialog';
import {ImportDevicePhoneBookEntriesDialogProps} from './ImportDevicePhoneBookEntriesDialog.utils';
import ImportDevicePhoneBookEntriesDialog from './ImportDevicePhoneBookEntriesDialog';
import {convertUtcToUserLocalTime} from '../../utils/dateWithTimezoneConversion';
import {DEVICE_PHONE_BOOK_NOT_FOUND_API_ERROR} from '../../store/sagas/devices/saga';
import NotFound from '../NotFound/NotFound';
import PermissionPlaceholder from '../../components/PermissionProvider/PermissionPlaceholder';

const DevicePhoneBookDetails: React.FC<DevicePhoneBookDetailsProps> = ({
    id
}) => {
    const classes = useStyle();
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const [deletePhoneBookDialog, setDeletePhoneBookDialog] = useState<{
        isOpen: boolean;
        i_ua_config_directory?: number;
        name?: string;
    }>({
        isOpen: false
    });

    const [deleteDialog, setDeleteDialog] = useState<{
        isOpen: boolean;
        i_ua_config_directory_item?: number;
        name?: string;
    }>({
        isOpen: false
    });
    
    const [renamePhoneBookDialog, setRenamePhoneBookDialog] = useState<RenameDevicePhoneBookDialogProps>({
        isOpen: false
    });
    
    const [showAddNewDevicePhoneEntryBookDialog, setShowAddNewDevicePhoneEntryBookDialog] 
        = useState<NewPhoneBookEntryDialogProps>({
        isOpen: false,
    });
    
    const [importPhoneBookEntriesDialog, setImportPhoneBookEntriesDialog] = useState<ImportDevicePhoneBookEntriesDialogProps>({
        isOpen: false
    });

    const {
        phoneBookDetails,
        isLoadingPhoneBookDetails,
        isUpdatingPhoneBook,
        phoneBookDetailsApiError,
        isDeletingPhoneBook = false,
    } = useSelector((state: ReduxState) => state.devices);

    const timezoneOffset = useSelector<ReduxState, number>(
        (state) => state.generic.sessionData?.tz_offset || 0,
    );

    const userDateTimeFormat = useSelector<ReduxState, string>(
        (state) =>
            state.generic.globalCustomerInfo?.customer_info
                ?.out_date_time_format || '',
    );

    const onChevronClick = useCallback(() => {
        history.push(Routes.DevicePhoneBooks);
    }, []);

    const onFinishAddEditEntry = useCallback((reload: boolean) => {
        setShowAddNewDevicePhoneEntryBookDialog({
            isOpen: false,
            editRecord: undefined
        });
        if(reload)
        {
            fetchData();
        }
    }, [setShowAddNewDevicePhoneEntryBookDialog]);

    const handleAddNewDevicePhoneBookEntryClick = useCallback((editRecord: PhoneDirectoryRecord | undefined = undefined ) => {
        setShowAddNewDevicePhoneEntryBookDialog({
            isOpen: true,
            clickOnClose: onFinishAddEditEntry,
            editRecord: editRecord
        })
    }, [setShowAddNewDevicePhoneEntryBookDialog, onFinishAddEditEntry]);

    const handleDeleteRecord = (obj: PhoneDirectoryRecord) => {
        setDeleteDialog({isOpen: true, 
            i_ua_config_directory_item: obj.i_ua_config_directory_item,
            name: (obj.first_name + ' ' + obj.last_name).trim(),
        });
    };

    const fetchData = useCallback(() => {
        dispatch(actions.getPhoneBookDetails.request({
                i_ua_config_directory: id,
                get_records: YesNo.Yes,
                get_total_number: YesNo.Yes,
                limit: 1000,
                offset: 0,
                response_format: {
                    return_in_file: YesNo.No
                }
            })
        );
    }, [id]);

    useEffect(() => {
        if (id && !isLoadingPhoneBookDetails && !phoneBookDetails && !phoneBookDetailsApiError) {
            fetchData();
        }
    }, [id, isLoadingPhoneBookDetails, phoneBookDetails, phoneBookDetailsApiError]);

    const closeDeleteDialog = () => {
        setDeleteDialog({
            isOpen: false,
            i_ua_config_directory_item: deleteDialog?.i_ua_config_directory_item,
            name: deleteDialog?.name
        });
        setTimeout(() => {
            setDeleteDialog({
                isOpen: false,
                i_ua_config_directory_item: undefined,
                name: undefined
            });
        }, 200);
    };

    const handleDeleteRecordAction = useCallback(() => {
        if(phoneBookDetails && deleteDialog) {
            dispatch(actions.updatePhoneBookDetails.request({
                ...phoneBookDetails,
                total_records: phoneBookDetails.total_records - 1,
                directory_records: phoneBookDetails.directory_records
                    ?.filter(e => e.i_ua_config_directory_item != deleteDialog.i_ua_config_directory_item) ?? [],
                callback: () => {
                    closeDeleteDialog();
                    fetchData();
                    showToast(t<string>('screens:devicePhoneBooks.devicePhoneBookRecordDeleted'));
                }
            }));
        }
    }, [deleteDialog]);

    const closeDeletePhoneBookDialog = () => {
        setDeletePhoneBookDialog({
            isOpen: false,
            i_ua_config_directory: 0,
            name: deletePhoneBookDialog?.name
        });
        setTimeout(() => {
            setDeletePhoneBookDialog({
                isOpen: false,
                i_ua_config_directory: undefined,
                name: undefined
            });
        }, 200);
    };
    

    const handleDeletePhoneBook = useCallback(() => {
        dispatch(actions.deleteDevicePhoneBook.request({
            i_ua_config_directory: deletePhoneBookDialog.i_ua_config_directory || 0,
            callback: () => {
                closeDeletePhoneBookDialog();
                onChevronClick();
                showToast(t<string>('screens:devicePhoneBooks.devicePhoneBookDeleted'));
            },
            errorCallback: (err: APIErrorInterface) => {
                closeDeletePhoneBookDialog();
                if(err.faultcode === 'Server.UA.phone_directory_not_removed') {
                    showToast(t<string>('errors:devicePhoneBooks.devicePhoneBookCantBeDeleted', {
                        phoneBookName: deletePhoneBookDialog.name ?? ''
                    }));
                }
                else {
                    showToast(err.faultstring);
                }
            },
        }));
    }, [deletePhoneBookDialog]);
    
    const handleResumeProvisioning = useCallback(() => {
        if(phoneBookDetails)
        {
            dispatch(actions.updatePhoneBookDetails.request({
                ...phoneBookDetails,
                state: 'active',
                callback: () => {
                    const toast = t<string>('screens:devicePhoneBooks.devicePhoneBookHasBeenActivated');
                    showToast(toast);
                    fetchData();
                }
            }));
        }
    }, [phoneBookDetails, fetchData]);

    const onSuccessUpdated = useCallback(() => {
        setRenamePhoneBookDialog({
            isOpen: false,
            directory: undefined
        });

    }, [setRenamePhoneBookDialog]);

    const handleRenamePhoneBook = useCallback(() => {
        setRenamePhoneBookDialog({
            isOpen: true,
            directory: phoneBookDetails,
            clickOnClose: onSuccessUpdated
        });
    }, [phoneBookDetails, setRenamePhoneBookDialog]);
    
    const onSuccessUploaded = useCallback(() => {
        setImportPhoneBookEntriesDialog({
            isOpen: false,
            directory: undefined
        });

    }, [setImportPhoneBookEntriesDialog]);

    const handleImportFromFile = useCallback(() => {
        setImportPhoneBookEntriesDialog({
            isOpen: true,
            directory: phoneBookDetails,
            clickOnClose: onSuccessUploaded
        });
    }, [phoneBookDetails, setImportPhoneBookEntriesDialog]);

    const handleDownloadEntries = useCallback(() => {
        if(!phoneBookDetails) return;
        dispatch(actions.getPhoneBookDetails.request({
            get_records: YesNo.Yes,
            get_total_number: YesNo.Yes,
            i_ua_config_directory: phoneBookDetails.i_ua_config_directory,
            offset: 0,
            fileName: phoneBookDetails.name + '_entries',
            response_format: {
                file_format: 'csv',
                return_in_file: YesNo.Yes,
            }
        }));
    }, [phoneBookDetails]);

    const isAutoSyncDirectory = useMemo(() => 
        (phoneBookDetails?.auto_sync ?? YesNo.No) === YesNo.Yes
    , [phoneBookDetails]);

    const isLoading = useMemo(() => {
        return isLoadingPhoneBookDetails || isUpdatingPhoneBook;
    }, [isLoadingPhoneBookDetails, isUpdatingPhoneBook]);

    const date = useMemo(() => phoneBookDetails ? convertUtcToUserLocalTime(
            phoneBookDetails.last_active_update,
            timezoneOffset || 0,
            userDateTimeFormat,
        ).split(' ') : [], [timezoneOffset, phoneBookDetails, userDateTimeFormat]);

    const columns = useMemo(() =>
        generateColumns(
            t,
            classes,
            handleDeleteRecord,
            isAutoSyncDirectory,
            handleAddNewDevicePhoneBookEntryClick
        ), [isAutoSyncDirectory]);

    if(phoneBookDetailsApiError?.faultcode === DEVICE_PHONE_BOOK_NOT_FOUND_API_ERROR) {
        return (<NotFound/>);
    }

    if (isLoading) {
        return (
            <PermissionPlaceholder permission={Permission.Inventory.DevicePhoneBooks.value}>
                <div className={classes.loader}>
                    <Loader dataQa={'device-details-loader'}/>
                </div>
            </PermissionPlaceholder>);
    }

    return (
        <PermissionProvider permission={Permission.Inventory.DevicePhoneBooks.value}>
            <div className={classes.mainContainer}>
                <div className={classes.scrollable}>
                    <div className={classes.headerContainer}>
                        <div className={classes.chevron}>
                            <CustomizedIconButton
                                className={classes.arrow}
                                onClick={onChevronClick}
                                skipPermission
                            >
                                <ChevronLeft />
                            </CustomizedIconButton>
                        </div>
                        <div className={classes.header}>
                            <PermissionProvider permission={Permission.Inventory.DevicePhoneBooks.value}>
                                <Header
                                    title={phoneBookDetails?.name ?? ''}
                                    totalNumber={phoneBookDetails?.total_records ?? 0}
                                    dataQa={'device-phone-book-list-view-header'}
                                    customClass={classes.header}
                                    plusIcon={true}
                                    buttonText={t('screens:devicePhoneBooks:addEntry')}
                                    buttonOnClick={() => {handleAddNewDevicePhoneBookEntryClick(undefined);}}
                                    showDetails={false}
                                    customButtonClass={classes.addNewButton}
                                    disabledButton={isAutoSyncDirectory}
                                    customDisabledTooltipText={t<string>('errors:devicePhoneBooks.cantUpdateAutoSyncDirectory')}
                                    ignoreSv
                                >
                                    <MoreMenu hideMoreOptionsButton={false} customMenuClass={classes.moreMenuButton}>
                                        <MenuItem key="rename-phone-book" onClick={handleRenamePhoneBook}
                                                className={classes.dropdownMenuItem}
                                        >
                                            {t('screens:devicePhoneBooks.renamePhoneBook')}
                                        </MenuItem>
                                        <MenuItem key="import-from-file" onClick={handleImportFromFile}
                                                className={classes.dropdownMenuItem}
                                        >
                                            {t('screens:devicePhoneBooks.importEntriesFromFile')}
                                        </MenuItem>
                                        <MenuItem key="download-phone-book-entries" onClick={handleDownloadEntries}
                                                className={classes.dropdownMenuItem}
                                        >
                                            {t('screens:devicePhoneBooks.downloadTheEntries')}
                                        </MenuItem>
                                    </MoreMenu>
                                </Header>
                            </PermissionProvider>
                        </div>
                    </div>
                    <DataGrid<PhoneDirectoryRecord>
                        className={classes.table}
                        classes={{tableContainer: classes.tableContainer}}
                        columns={columns}
                        data={phoneBookDetails?.directory_records || []}
                        rowCount={phoneBookDetails?.total_records || 0}
                        loading={isLoading}
                        paginationMode={PaginationMode.Client}
                        centeredRows
                        narrowRows
                        initialPageSize={10}
                        getItemId={(itm) => itm.i_ua_config_directory_item}
                    />
                    
                    <AlertDialog
                        className={classes.deleteDialog}
                        contentClass={classes.contentDialog}
                        isOpen={deleteDialog.isOpen}
                        dataQa={'delete-phone-book-entry-dialog'}
                        hideHeader
                        description={t('screens:devicePhoneBooks.deleteRecordRule', {
                            name: deleteDialog.name || '',
                        })}
                        dialogActionsButtons={[
                            <DialogButton
                                key="cancel"
                                label={t('common:cancel')}
                                onClick={closeDeleteDialog}
                            />,
                            <DialogButton
                                key="delete"
                                label={t('common:delete')}
                                onClick={handleDeleteRecordAction}
                            />,
                        ]}
                    />
                
                </div>
                
                <NewPhoneBookEntryDialog {...showAddNewDevicePhoneEntryBookDialog} />

                <RenameDevicePhoneBookDialog {...renamePhoneBookDialog} />

                <ImportDevicePhoneBookEntriesDialog {...importPhoneBookEntriesDialog} />
                
                <FormActions
                    isSaveActive={!isAutoSyncDirectory && phoneBookDetails?.state !== 'active'}
                    onSave={handleResumeProvisioning}
                    hideCancel={true}
                    hideDelete={false}
                    editPermission={Permission.Inventory.DevicePhoneBooks.value}
                    deleteTitle={t('screens:devicePhoneBooks.deleteDevicePhoneBook')}
                    onDelete={() => {
                        setDeletePhoneBookDialog({
                            isOpen: true,
                            i_ua_config_directory: phoneBookDetails?.i_ua_config_directory,
                            name: phoneBookDetails?.name
                        });
                    }}
                    deletePermission={Permission.Inventory.DevicePhoneBooks.value}
                    className={classes.actionButtonsContainer}
                    saveButtonTitle={t('screens:devicePhoneBooks.resumeProvisioning')}
                    saveButtonClass={classes.saveButtonClass}
                >
                    <div className={classes.lastUpdatedContainer}>
                        <div className={classes.infoIcon}><Info /></div>
                        <div className={classes.infoText}>{t('screens:devicePhoneBooks.lastUpdatedOn', {
                            date: date.length >= 2 ? date[0] : '',
                            time: date.length >= 2 ? date[1] : '',
                            am: date.length >= 3 ? date[2] : ''
                        })}</div>
                    </div>
                </FormActions>
                
                <AlertDialog
                    className={classes.deleteDialog}
                    contentClass={classes.contentDialog}
                    isOpen={deletePhoneBookDialog.isOpen}
                    dataQa={'delete-phone-book-dialog'}
                    hideHeader
                    description={t('screens:devicePhoneBooks.deleteRule', {
                        name: deletePhoneBookDialog.name || '',
                    })}
                    dialogActionsButtons={[
                        <DialogButton
                            key="cancel"
                            label={t('common:cancel')}
                            onClick={closeDeletePhoneBookDialog}
                            hidden={isDeletingPhoneBook || deletePhoneBookDialog?.i_ua_config_directory === 0}
                        />,
                        <DialogButton
                            key="delete"
                            label={t('common:delete')}
                            onClick={handleDeletePhoneBook}
                            loadingAnimation={isDeletingPhoneBook || deletePhoneBookDialog.i_ua_config_directory === 0}
                        />,
                    ]}
                />

            </div>
        </PermissionProvider>
    );
};

export default DevicePhoneBookDetails;
